/**
 * What Invite People offers, by the backend's rules (orgInviteMember's
 * programInvitePlan). A program for roles takes its learners through its
 * roles: someone new is invited as a learner only with their team and one of
 * the program's roles in it, and they join the program through that role
 * when they sign up. Someone already in the organization can only be added
 * to it as an admin (Existing). Any other program takes a learner or an admin
 * either way, with a team optional.
 *
 * No React here, so it can be tested on its own (inviteChoices.test.ts).
 */

export type InviteWay = 'existing' | 'new';
export type ProgramRoleValue = 'mentee' | 'admin';

export interface InviteTeam {
  id: string;
  name: string;
}

/** A team's role, as Capability Operations lists it: ready once its capabilities and levels are confirmed. */
export interface InviteRole {
  id: string;
  teamId: string;
  name: string;
  ready: boolean;
}

/** The program roles to choose from: only admin when adding someone existing to a program for roles. */
export const programRoleValues = (forRoles: boolean, way: InviteWay): ProgramRoleValue[] =>
  forRoles && way === 'existing' ? ['admin'] : ['mentee', 'admin'];

/** Whether the person joins as a learner through a role: someone new, as a learner, in a program for roles. */
export const joinsThroughRole = (forRoles: boolean, way: InviteWay, programRole: ProgramRoleValue): boolean =>
  forRoles && way === 'new' && programRole === 'mentee';

/**
 * The roles to choose from in a team: its ready ones (people are invited to a
 * role once it is confirmed, as in Invite Team), and only the program's own
 * when the person joins through one (`programRoleIds`; null otherwise).
 */
export function roleChoices(
  roles: InviteRole[],
  teamId: string | null | undefined,
  programRoleIds: string[] | null
): InviteRole[] {
  if (!teamId) return [];
  return roles.filter((r) => r.teamId === teamId && r.ready && (!programRoleIds || programRoleIds.includes(r.id)));
}

/** The teams to choose from: every one, or only those with a ready role of the program's when the person joins through one. */
export function teamChoices(teams: InviteTeam[], roles: InviteRole[], programRoleIds: string[] | null): InviteTeam[] {
  if (!programRoleIds) return teams;
  return teams.filter((t) => roleChoices(roles, t.id, programRoleIds).length > 0);
}

/**
 * Whether the team and role chosen can be sent: a team always needs the role
 * held in it, and joining through a role needs both, the team one of
 * `teamChoices` and the role one of `roleChoices`.
 */
export function teamAndRoleValid(input: {
  teams: InviteTeam[];
  roles: InviteRole[];
  teamId: string | null | undefined;
  roleId: string | null | undefined;
  /** The program's roles when the person joins through one; null otherwise. */
  programRoleIds: string[] | null;
}): boolean {
  const { teamId, roleId, programRoleIds } = input;
  if (!teamId) return !roleId && !programRoleIds;
  if (!roleId) return false;
  if (!teamChoices(input.teams, input.roles, programRoleIds).some((t) => t.id === teamId)) return false;
  return roleChoices(input.roles, teamId, programRoleIds).some((r) => r.id === roleId);
}
